import moment from 'moment'
import { addOrdinalSuffix } from '@quickmd-llc/frontend-utils'
import type { WaitingRoomV2VisitListItemDoctorApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'

export type GroupedVisits = {
  label: string
  items: WaitingRoomV2VisitListItemDoctorApiOutContract[]
}

export function formatVisitTime(dateTime?: string | null): string {
  if (!dateTime) return '--'
  return moment(dateTime).format('h:mma')
}

export function formatVisitTimeOrDate(dateTime?: string | null): string {
  if (!dateTime) return '--'
  const m = moment(dateTime).local()
  if (m.isBefore(moment().startOf('day'))) return m.format('MMM D')
  return m.format('h:mma')
}

export function getDateSectionLabel(dateTime?: string | null): string {
  if (!dateTime) return '--'
  const m = moment(dateTime).local()
  if (m.isSame(moment(), 'day')) return 'Today'
  if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday'
  return m.format('MMM D, YYYY')
}

export function groupVisitsByDateAsc(list: WaitingRoomV2VisitListItemDoctorApiOutContract[]): GroupedVisits[] {
  const sorted = [...list].sort((a, b) => new Date(a.DateTime).getTime() - new Date(b.DateTime).getTime())
  const sections: GroupedVisits[] = []
  const index = new Map<string, number>()
  sorted.forEach((visit) => {
    const label = getDateSectionLabel(visit.DateTime)
    if (!index.has(label)) {
      index.set(label, sections.length)
      sections.push({ label, items: [] })
    }
    // @ts-expect-error TS2532 -- MIGRATION: not approved, fix when encountered
    sections[index.get(label)!].items.push(visit)
  })
  return sections
}

export function groupVisitsByPreviousAndToday(list: WaitingRoomV2VisitListItemDoctorApiOutContract[]): GroupedVisits[] {
  const sorted = [...list].sort((a, b) => new Date(a.DateTime).getTime() - new Date(b.DateTime).getTime())
  const today = moment().startOf('day')
  const previous: WaitingRoomV2VisitListItemDoctorApiOutContract[] = []
  const todayItems: WaitingRoomV2VisitListItemDoctorApiOutContract[] = []

  sorted.forEach((visit) => {
    if (moment(visit.DateTime).isBefore(today)) {
      previous.push(visit)
    } else {
      todayItems.push(visit)
    }
  })

  const sections: GroupedVisits[] = []
  if (previous.length) sections.push({ label: 'Previous', items: previous })
  if (todayItems.length) sections.push({ label: 'Today', items: todayItems })
  return sections
}

/**
 * Drop any visits whose VisitId is in `excludedIds`. Used to optimistically hide just-completed
 * online visits from the dashboard list + badge count until the MQTT-driven refetch removes them
 * server-side. Returns the original reference when nothing is excluded so memoized consumers stay
 * stable.
 */
export function excludeVisitsByIds(
  list: WaitingRoomV2VisitListItemDoctorApiOutContract[],
  excludedIds: Set<string>,
): WaitingRoomV2VisitListItemDoctorApiOutContract[] {
  if (excludedIds.size === 0) return list
  return list.filter((visit) => !excludedIds.has(visit.VisitId))
}

/**
 * Prune the optimistic "just-deferred → show Start" mask against the freshly-fetched list: keep an
 * id only while its visit is still present AND still 'pending' (in-progress). Once the visit is
 * gone or the backend reflects the release (non-'pending'), the override is dropped so a later
 * re-claim correctly renders "Continue". Returns the original reference when nothing changed.
 */
export function pruneStartMask(ids: Set<string>, list: WaitingRoomV2VisitListItemDoctorApiOutContract[]): Set<string> {
  if (ids.size === 0) return ids
  const next = new Set<string>()
  ids.forEach((id) => {
    const visit = list.find((v) => v.VisitId === id)
    if (visit && visit.Status === 'pending') next.add(id)
  })
  return next.size === ids.size ? ids : next
}

/**
 * Prune the optimistic "just-completed → hide row" mask: drop an id once its visit is gone from the
 * freshly-fetched list (the completion has propagated server-side). Returns the original reference
 * when nothing changed.
 */
export function pruneRemovedMask(
  ids: Set<string>,
  list: WaitingRoomV2VisitListItemDoctorApiOutContract[],
): Set<string> {
  if (ids.size === 0) return ids
  const next = new Set<string>()
  ids.forEach((id) => {
    if (list.some((v) => v.VisitId === id)) next.add(id)
  })
  return next.size === ids.size ? ids : next
}

export function formatVisitOccurrence(occurrenceNumber?: number | null): string {
  if (!occurrenceNumber) return ''
  return `${addOrdinalSuffix(occurrenceNumber)} visit`
}

export function mapVisitToPatientCard(
  visit: WaitingRoomV2VisitListItemDoctorApiOutContract,
): WaitingRoomV2VisitListItemDoctorApiOutContract['Patient'] & {
  Address: { StateCode?: string | null }
  PhoneNumber: { Number?: string | null }
} {
  // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
  return { ...visit.Patient, Address: { StateCode: visit.StateCode }, PhoneNumber: visit.Patient.Phone }
}
